import React, {useContext, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import {useForm} from "react-hook-form";
import {FamilyContext} from '../Context/FamilyHeadContext';
import {API_URL, doApiMethod, loginTimeExtended, loginTimeStandard, TOKEN_KEY} from "../services/apiService";
import {LoadingFallback} from "../App";

const LoginPage = () => {
    const { pathPrefix } = useContext(FamilyContext)
    const { register, handleSubmit } = useForm();
    const { setIsAuthenticated } =  useContext(FamilyContext);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const onSubForm = async (_bodyData) => {
        if (
            !/^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(_bodyData.email) ||
            !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,30}$/.test(_bodyData.password)
        ) {
            setErrorMessage('לתשומת ליבך: מייל או סיסמה שגויים, נסה שנית');
            return;
        }
        setLoading(true);
        const url = API_URL + '/users/logIn';
        try {
            const data = await doApiMethod(url, 'POST', _bodyData);
            const token = data.token;
            const expirationTime = _bodyData.rememberMe ? loginTimeExtended : loginTimeStandard;
            const expirationDate = new Date(new Date().getTime() + expirationTime);
            localStorage.setItem(TOKEN_KEY, token);
            localStorage.setItem('expirationDate', expirationDate.toString());
            localStorage.setItem('loginDate', new Date().toString());
            setIsAuthenticated(true);
            navigate(`${pathPrefix}admin`);
        } catch (error) {
            console.log('error:', error);
            const { response } = error;
            if (response && response.status === 401) {
                setErrorMessage('לתשומת ליבך: מייל או סיסמה שגויים, נסה שנית');
            } else {
                setErrorMessage('שגיאה בהתחברות, נסה שנית');
            }
        } finally {
            setLoading(false); // End loading
        }
    };

    return (
        <div className="min-h-screen py-12 px-4 bg-[#f8f5f1]">
            <div className="max-w-md mx-auto">
                <div className="bg-white rounded-2xl p-8 shadow-[0_4px_20px_rgba(201,166,108,0.08)]">
                    {loading ? (
                        <LoadingFallback />
                    ) : (
                        <>
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-heebo font-bold text-[#937c5d]">ברוכים הבאים</h2>
                                <p className="text-[#4a3f30]/70 font-heebo text-lg">התחבר לאתר המשפחתי</p>
                            </div>

                            {errorMessage && (
                                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                                    {errorMessage}
                                </div>
                            )}

                            <form onSubmit={handleSubmit(onSubForm)} className="space-y-6" dir="rtl">
                                <div className="relative">
                                    <label className="block text-[#4a3f30] font-heebo text-md mb-2">
                                        אימייל
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="email"
                                            {...register('email')}
                                            className={`w-full p-4 pl-12 rounded-xl border-2 border-[#e1ddd7] 
                                                bg-white/90 text-[#4a3f30] placeholder-[#937c5d]/60 font-heebo
                                                focus:outline-none focus:border-[#c9a66c] focus:ring-2 focus:ring-[#c9a66c]/20
                                                transition-all duration-300`}
                                            placeholder="הכנס את האימייל שלך"
                                            dir="ltr"
                                            autoComplete="username"
                                        />
                                        <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#937c5d]/60"
                                              size={20}/>
                                    </div>
                                </div>

                                <div className="relative">
                                    <label className="block text-[#4a3f30] font-heebo text-md mb-2">
                                        סיסמה
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            {...register('password')}
                                            className={`w-full p-4 pl-12 rounded-xl border-2 
                                                border-[#e1ddd7]
                                                bg-white/90 text-[#4a3f30] placeholder-[#937c5d]/60 font-heebo
                                                focus:outline-none focus:border-[#c9a66c] focus:ring-2 focus:ring-[#c9a66c]/20
                                                transition-all duration-300`}
                                            placeholder="הכנס את הסיסמה שלך"
                                            dir="ltr"
                                            autoComplete="current-password"
                                        />
                                        <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#937c5d]/60"
                                              size={20}/>
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#937c5d]/60
                                                hover:text-[#937c5d] transition-colors duration-300"
                                        >
                                            {showPassword ? <EyeOff size={20}/> : <Eye size={20}/> }
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2 space-x-reverse">
                                        <input
                                            type="checkbox"
                                            id="remember"
                                            {...register('rememberMe')}
                                            className="w-4 h-4 rounded border-[#e1ddd7] text-[#c9a66c]
                                                focus:ring-[#c9a66c] focus:ring-offset-0"
                                        />
                                        <label htmlFor="remember" className="text-md font-heebo text-[#4a3f30]">
                                            זכור אותי
                                        </label>
                                    </div>
                                    {/*<Link to="#" className="text-md font-heebo text-[#937c5d] hover:text-[#c9a66c]*/}
                                    {/*    transition-colors duration-300">*/}
                                    {/*    שכחת סיסמה?*/}
                                    {/*</Link>*/}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{
                                        background: 'linear-gradient(145deg, #d4b785, #c9a66c)'
                                    }}
                                    className="w-full text-white py-4 rounded-xl font-medium shadow-sm
                                        text-lg flex items-center justify-center font-heebo
                                        hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed
                                        focus:outline-none focus:ring-2 focus:ring-[#c9a66c]/50
                                        transition-all duration-300"
                                >
                                    התחברות
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;